/**
 * MorphLoading — 4-square morphing loader.
 *
 * Four bg-black squares positioned in the four corners of a square
 * container. Each square translates around the perimeter while its
 * border-radius morphs from square to circle and back, producing a
 * continuous "morph" motion that the four squares hand off to each
 * other 90° out of phase.
 *
 * Replaces the legacy `mm:ss` countdown timer in the drafting/review
 * panels because long-running gpt-5.5 drafts (~2-3 min) made the raw
 * timer feel pessimistic.
 *
 * Sizes (uniform scale — each step doubles the box):
 *   • sm  — 32 px box, 10 px squares
 *   • md  — 48 px box, 14 px squares
 *   • lg  — 64 px box, 20 px squares  (default)
 *
 * Usage:
 *   <MorphLoading size="lg" />
 *   <MorphLoading size="md" className="opacity-80" />
 */
import React from 'react';
import { cn } from '@/lib/utils';

const SIZE_PRESETS = {
  sm: { box: 32, square: 10 },
  md: { box: 48, square: 14 },
  lg: { box: 64, square: 20 },
};

export function MorphLoading({ size = 'lg', className, 'data-testid': testId = 'morph-loading' }) {
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.lg;
  const { box, square } = preset;
  const span = box - square; // distance each cube travels along each axis

  return (
    <div
      className={cn('morph-loader', className)}
      style={{ width: box, height: box }}
      role="status"
      aria-label="loading"
      data-testid={testId}
    >
      <span className="morph-loader__cube morph-loader__cube--0" />
      <span className="morph-loader__cube morph-loader__cube--1" />
      <span className="morph-loader__cube morph-loader__cube--2" />
      <span className="morph-loader__cube morph-loader__cube--3" />

      <style>{`
        .morph-loader {
          position: relative;
          display: inline-block;
        }
        .morph-loader__cube {
          position: absolute;
          width: ${square}px;
          height: ${square}px;
          background: #000;
          animation-duration: 2s;
          animation-iteration-count: infinite;
          animation-timing-function: ease-in-out;
        }
        .morph-loader__cube--0 {
          top: 0;
          left: 0;
          animation-name: morph-0-${box};
        }
        .morph-loader__cube--1 {
          top: 0;
          left: ${span}px;
          animation-name: morph-1-${box};
        }
        .morph-loader__cube--2 {
          top: ${span}px;
          left: ${span}px;
          animation-name: morph-2-${box};
        }
        .morph-loader__cube--3 {
          top: ${span}px;
          left: 0;
          animation-name: morph-3-${box};
        }
        @keyframes morph-0-${box} {
          0%   { transform: translate(0, 0);            border-radius: 0%; }
          25%  { transform: translate(${span}px, 0);    border-radius: 50%; }
          50%  { transform: translate(${span}px, ${span}px); border-radius: 0%; }
          75%  { transform: translate(0, ${span}px);    border-radius: 50%; }
          100% { transform: translate(0, 0);            border-radius: 0%; }
        }
        @keyframes morph-1-${box} {
          0%   { transform: translate(0, 0);             border-radius: 0%; }
          25%  { transform: translate(0, ${span}px);     border-radius: 50%; }
          50%  { transform: translate(-${span}px, ${span}px); border-radius: 0%; }
          75%  { transform: translate(-${span}px, 0);    border-radius: 50%; }
          100% { transform: translate(0, 0);             border-radius: 0%; }
        }
        @keyframes morph-2-${box} {
          0%   { transform: translate(0, 0);              border-radius: 0%; }
          25%  { transform: translate(-${span}px, 0);     border-radius: 50%; }
          50%  { transform: translate(-${span}px, -${span}px); border-radius: 0%; }
          75%  { transform: translate(0, -${span}px);     border-radius: 50%; }
          100% { transform: translate(0, 0);              border-radius: 0%; }
        }
        @keyframes morph-3-${box} {
          0%   { transform: translate(0, 0);              border-radius: 0%; }
          25%  { transform: translate(0, -${span}px);     border-radius: 50%; }
          50%  { transform: translate(${span}px, -${span}px); border-radius: 0%; }
          75%  { transform: translate(${span}px, 0);      border-radius: 50%; }
          100% { transform: translate(0, 0);              border-radius: 0%; }
        }
      `}</style>
    </div>
  );
}

export default MorphLoading;
